import { useTranslation } from 'react-i18next';
import { useScrollReveal } from '@/hooks/useScrollReveal';

export default function CTASection() {
  const { t } = useTranslation();
  const { ref, isVisible } = useScrollReveal(0.1);

  return (
    <section
      id="contacto"
      className="relative min-h-[500px] md:min-h-[600px] flex items-center overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0">
        <img
          src="https://readdy.ai/api/search-image?query=dark%20premium%20steakhouse%20kitchen%20raw%20meat%20display%20butcher%20counter%20warm%20golden%20lighting%20professional%20photography%20moody%20atmosphere%20cinematic%20depth&width=1440&height=700&seq=cta-bg&orientation=landscape"
          alt="Visita L&J PREMIUM MEATS LLC en Manhattan — Carnicería Latina con los mejores cortes frescos"
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-brand-dark/60" />
      </div>

      {/* Content */}
      <div
        ref={ref}
        className={`relative z-10 w-full px-4 md:px-8 lg:px-12 text-center transition-all duration-1000 ${
          isVisible
            ? 'opacity-100 translate-y-0'
            : 'opacity-0 translate-y-10'
        }`}
      >
        <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-4 leading-tight">
          {t('cta_title')}
          <br />
          <span className="text-brand-gold">{t('cta_title_highlight')}</span>
        </h2>
        <p className="text-white/70 text-base md:text-lg max-w-2xl mx-auto mb-8">
          {t('cta_desc')}
        </p>

        <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-10">
          <a
            href="https://www.google.com/maps/search/?api=1&query=1506%20St%20Nicholas%20Ave%2C%20New%20York%2C%20NY%2010033"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-brand-gold text-brand-dark font-semibold rounded-full hover:bg-brand-gold/90 transition-all duration-300 hover:scale-[1.02] whitespace-nowrap"
          >
            <i className="ri-map-pin-line" />
            <span>{t('cta_map')}</span>
          </a>
          <a
            href="tel:+13473606636"
            className="inline-flex items-center gap-3 px-8 py-4 border-2 border-white/30 text-white font-semibold rounded-full hover:bg-white/10 transition-all duration-300 whitespace-nowrap"
          >
            <i className="ri-phone-line" />
            <span>{t('cta_call')}</span>
          </a>
        </div>

        {/* Info cards */}
        <div className="grid sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
          {/* Hours */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1">
            <i className="ri-time-line text-brand-gold text-xl mb-2 block" />
            <div className="text-white font-semibold text-sm mb-1">
              {t('info_hours')}
            </div>
            <div className="text-white/60 text-xs">
              {t('info_hours_week')}
              <br />
              {t('info_hours_sun')}
            </div>
          </div>
          {/* Address */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1">
            <i className="ri-map-pin-2-line text-brand-gold text-xl mb-2 block" />
            <div className="text-white font-semibold text-sm mb-1">
              {t('info_address')}
            </div>
            <div className="text-white/60 text-xs">
              1506 St Nicholas Ave
              <br />
              New York, NY 10033
            </div>
          </div>
          {/* Phone */}
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-4 md:p-5 hover:bg-white/15 transition-all duration-300 hover:-translate-y-1">
            <i className="ri-phone-line text-brand-gold text-xl mb-2 block" />
            <div className="text-white font-semibold text-sm mb-1">
              {t('info_phone')}
            </div>
            <div className="text-white/60 text-xs leading-relaxed">
              (347) 360-6636
              <br />
              (917) 504-9095
              <br />
              (917) 708-4424
            </div>
          </div>
        </div>

        {/* Map */}
        <div className="mt-10 max-w-3xl mx-auto">
          <div className="bg-white/10 backdrop-blur-sm rounded-xl p-3 md:p-4">
            <div className="aspect-[16/9] w-full rounded-lg overflow-hidden">
              <iframe
                src="https://maps.google.com/maps?q=1506+St+Nicholas+Ave,+New+York,+NY+10033&t=&z=17&ie=UTF8&iwloc=&output=embed"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="L&J PREMIUM MEATS LLC — ubicación"
                className="w-full h-full"
              />
            </div>
            <div className="flex items-center justify-between mt-3 px-1">
              <span className="text-white/60 text-xs">
                1506 St Nicholas Ave, New York, NY 10033
              </span>
              <a
                href="https://www.google.com/maps/dir/?api=1&destination=1506%20St%20Nicholas%20Ave%2C%20New%20York%2C%20NY%2010033"
                target="_blank"
                rel="noopener noreferrer"
                className="text-brand-gold text-xs font-medium hover:underline flex items-center gap-1"
              >
                <i className="ri-navigation-line" />
                Cómo Llegar
              </a>
            </div>
          </div>
        </div>

        {/* WhatsApp CTA */}
        <div className="mt-8 max-w-3xl mx-auto">
          <a
            href="https://wa.me/19175049095"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-3 px-8 py-4 bg-[#25D366] text-white font-semibold rounded-full hover:bg-[#20BA5A] transition-all duration-300 hover:scale-[1.02] whitespace-nowrap"
          >
            <i className="ri-whatsapp-line text-xl" />
            <span>{t('cta_whatsapp')}</span>
          </a>
        </div>

        {/* Team */}
        <div className="mt-10 flex flex-wrap items-center justify-center gap-6">
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="w-8 h-8 bg-brand-gold/20 rounded-full flex items-center justify-center">
              <i className="ri-user-3-line text-brand-gold text-sm" />
            </div>
            <span className="text-white/80 text-sm font-medium">Javier</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="w-8 h-8 bg-brand-gold/20 rounded-full flex items-center justify-center">
              <i className="ri-user-3-line text-brand-gold text-sm" />
            </div>
            <span className="text-white/80 text-sm font-medium">Luisa</span>
          </div>
          <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
            <div className="w-8 h-8 bg-brand-gold/20 rounded-full flex items-center justify-center">
              <i className="ri-user-3-line text-brand-gold text-sm" />
            </div>
            <span className="text-white/80 text-sm font-medium">Henry</span>
          </div>
        </div>
      </div>
    </section>
  );
}